import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import log from 'loglevel';
import { useAuth } from '../../hooks/useAuth';
import useNavigate from '../../hooks/useNavigate';
import STRING_KEYS from '../../language/keys';
import LoginContainer from './LoginContainer';
import LoginHead from './LoginHead';
import LoginInputs from './LoginInputs/LoginInputs';
import LoginAction from './LoginAction';
import LoginError from './LoginError';
import { initialValues, validationSchema } from './validation';

const TAG = '[LOGIN_PAGE]';

const LoginPage = (): JSX.Element => {
  const { t: translate } = useTranslation();
  const { goToPositions } = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, helpers): Promise<void> => {
      try {
        await login(values.email, values.password);
        goToPositions(true);
      } catch (err) {
        log.error(TAG, 'failed to login', err);
        helpers.setErrors({ errorMessage: translate(STRING_KEYS.LOGIN_ERROR) });
      }
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      goToPositions(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailInput = {
    error: Boolean(formik.touched.email && formik.errors.email),
    helperText: formik.touched.email && formik.errors.email,
    value: formik.values.email,
  };

  const passInput = {
    error: Boolean(formik.touched.password && formik.errors.password),
    helperText: formik.touched.password && formik.errors.password,
    value: formik.values.password,
  };

  return (
    <LoginContainer onSubmit={ formik.handleSubmit }>
      <LoginHead />
      <LoginInputs
        email={ emailInput }
        password={ passInput }
        onBlur={ formik.handleBlur }
        onChange={ formik.handleChange }
      />
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <LoginError message={ formik.errors.errorMessage } />
      <LoginAction isSubmitting={ formik.isSubmitting } />
    </LoginContainer>
  );
};

export default LoginPage;
